import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import { tmApi } from '../utils/taskManager';

const { Title, Text } = Typography;

const TaskManagerStatusesPage = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    /*
     * Статусы дублируются в справочник task_statuses
     * (/task-manager/dictionaries). Эта страница оставлена
     * для быстрой правки цветов; источник истины — справочник.
     */

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await tmApi.get(
                '/api/task-manager/statuses'
            );
            setRows(data.data || []);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки статусов'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const syncFromTasks = async () => {
        setLoading(true);
        try {
            const { data } = await tmApi.post(
                '/api/task-manager/statuses/sync'
            );
            message.success(
                `Найдено: ${data.found}, новых: ${data.created}`
            );
            await load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка синхронизации статусов'
            );
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({
            color: '#1677ff',
            sort_order: 100,
            is_active: true
        });
        setOpen(true);
    };

    const openEdit = record => {
        setEditing(record);
        form.setFieldsValue(record);
        setOpen(true);
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            if (editing) {
                await tmApi.put(
                    `/api/task-manager/statuses/${editing.id}`,
                    values
                );
                message.success('Статус обновлён');
            } else {
                await tmApi.post(
                    '/api/task-manager/statuses',
                    values
                );
                message.success('Статус создан');
            }
            setOpen(false);
            load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const remove = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/statuses/${id}`
            );
            message.success('Удалено');
            load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Статусы задач
                        </Title>
                        <Text type="secondary">
                            Статусы также в справочнике
                            «Статусы задач» на{' '}
                            <a href="/task-manager/dictionaries">
                                /task-manager/dictionaries
                            </a>
                        </Text>
                    </div>
                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={load}
                        >
                            Обновить
                        </Button>
                        <Button onClick={syncFromTasks}>
                            Подтянуть из задач
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                        >
                            Новый статус
                        </Button>
                    </Space>
                </Space>

                <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={rows}
                    pagination={false}
                    columns={[
                        {
                            title: 'Название',
                            dataIndex: 'name'
                        },
                        {
                            title: 'Цвет',
                            dataIndex: 'color',
                            width: 160,
                            render: (color, row) => (
                                <Tag
                                    style={{
                                        color: '#fff',
                                        background: color,
                                        borderColor: color,
                                        fontWeight: 600
                                    }}
                                >
                                    {row.name}
                                </Tag>
                            )
                        },
                        {
                            title: 'HEX',
                            dataIndex: 'color',
                            width: 120
                        },
                        {
                            title: 'Порядок',
                            dataIndex: 'sort_order',
                            width: 90
                        },
                        {
                            title: 'Активен',
                            dataIndex: 'is_active',
                            width: 90,
                            render: v =>
                                v ? (
                                    <Tag color="green">да</Tag>
                                ) : (
                                    <Tag>нет</Tag>
                                )
                        },
                        {
                            title: '',
                            width: 120,
                            render: (_, row) => (
                                <Space>
                                    <Button
                                        size="small"
                                        icon={
                                            <EditOutlined />
                                        }
                                        onClick={() =>
                                            openEdit(row)
                                        }
                                    />
                                    <Popconfirm
                                        title="Удалить статус?"
                                        onConfirm={() =>
                                            remove(row.id)
                                        }
                                    >
                                        <Button
                                            size="small"
                                            danger
                                            icon={
                                                <DeleteOutlined />
                                            }
                                        />
                                    </Popconfirm>
                                </Space>
                            )
                        }
                    ]}
                />

                <Modal
                    open={open}
                    title={
                        editing
                            ? `Статус #${editing.id}`
                            : 'Новый статус'
                    }
                    onCancel={() => setOpen(false)}
                    onOk={save}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="name"
                            label="Название"
                            rules={[{ required: true }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            name="color"
                            label="Цвет"
                            rules={[{ required: true }]}
                        >
                            <Input type="color" />
                        </Form.Item>
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активен"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </Layout>
    );
};

export default TaskManagerStatusesPage;
